import { Switch } from "shared/components/forms/Switch";
import { Stepper } from "../components/Stepper";
import { FormData, Views } from "../types";
import { useEffect } from "react";
import { useSettingsController } from "views/settings-integration/controller";
import { useAuthContext } from "providers/Auth";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { UnlistUnitTiming } from "api/landlord/templates";

export const DecisionSettingsView = ({
  view,
  flow,
  formData,
  updateFormData,
  address,
  disableAddApplicantsToPms,
  unlistUnitTiming
}: {
  formData: FormData;
  updateFormData: (updates: Partial<FormData>) => void;
  view: Views;
  flow: Views[];
  address: string;
  disableAddApplicantsToPms?: boolean;
  unlistUnitTiming?: UnlistUnitTiming;
}) => {
  const { unit } = useUnitsPropertiesDictionary();
  const { isEverySyncEnabled } = useSettingsController();
  const { type } = useAuthContext();

  // Hide toggles when:
  // - PMS sync is enabled (controlled externally)
  // - Timing is ON_FIRST_APPLICATION (unit was already disallowed on submission)
  const shouldHideToggles =
    isEverySyncEnabled ||
    unlistUnitTiming === UnlistUnitTiming.ON_FIRST_APPLICATION;

  useEffect(() => {
    if (isEverySyncEnabled)
      updateFormData({ unlistUnit: false, disallowApplications: false });
  }, [isEverySyncEnabled]);

  return (
    <div className="flex flex-col h-full">
      <Stepper view={view} flow={flow} />

      <div className="p-6">
        {shouldHideToggles ? null : (
          <>
            <Switch
              label={
                <>
                  Unlist {unit}
                  <span className="block mt-2 text-sm text-content-400">
                    Unlist {address} from the{" "}
                    {type.isBoomCRM ? "listing page" : "portfolio page"}
                  </span>
                </>
              }
              checked={formData.unlistUnit}
              onChange={(value) => updateFormData({ unlistUnit: value })}
              wrapperClassName="mb-6 !gap-4"
            />

            <Switch
              label={
                <>
                  Disallow applying for {unit}
                  <span className="block mt-2 text-sm text-content-400">
                    Acceptance of new applications will be paused for {address}
                  </span>
                </>
              }
              checked={formData.disallowApplications}
              onChange={(value) =>
                updateFormData({ disallowApplications: value })
              }
              wrapperClassName="mb-6 !gap-4"
            />
          </>
        )}

        <Switch
          label="Notify applicants about the decision via email"
          checked={formData.sendNotifications}
          onChange={(value) => updateFormData({ sendNotifications: value })}
          wrapperClassName="mb-6 !gap-4"
        />
        {disableAddApplicantsToPms || type.isManual ? null : (
          <Switch
            label="Add applicants to Property Management Software"
            checked={formData.addApplicantsToPMS}
            onChange={(value) => updateFormData({ addApplicantsToPMS: value })}
            wrapperClassName="mb-6 !gap-4"
          />
        )}
      </div>
    </div>
  );
};
